/**
 * Decides WHEN each guided tour starts. The overlay itself (`Tour.tsx`) only
 * renders whatever this has made active.
 *
 * Gates, in order:
 *  - signed in AND past onboarding — a tour over the profile form is noise, and
 *    `/api/me` must have landed or `toursSeen` would read as "seen nothing" and
 *    replay a tour the user has already done;
 *  - not already seen (server list ∪ this session's optimistic list);
 *  - its trigger condition is met (the main tour on arrival; the nested ones
 *    when the view they describe is first opened);
 *  - at least one of its anchors is on screen, retried briefly, because these
 *    controls mount asynchronously — the Signal pill waits on its own status
 *    fetch. The old hint gave up on that race and burned its "seen" flag.
 *
 * Only one tour runs at a time: a nested tour opened mid-main-tour waits.
 */
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import {
  TOUR_MAIN,
  TOUR_SIGNAL_ORDERS,
  TOUR_STRATEGIES,
  startTour,
  type TourId,
} from "@/state/slices/tourSlice";
import { api } from "@/api/client";
import { anchorEl, stepsFor } from "./tourSteps";

/** How long to keep looking for a tour's anchors before giving up for now. */
const ANCHOR_WAIT_MS = 6_000;

export function useTours(): void {
  const dispatch = useAppDispatch();
  const status = useAppSelector((s) => s.me.status);
  const profileComplete = useAppSelector((s) => s.me.profileComplete);
  const serverSeen = useAppSelector((s) => s.me.toursSeen);
  const justSeen = useAppSelector((s) => s.tour.justSeen);
  // Tours that stood aside this session (no anchors yet). NOT persisted — they
  // get another chance on the next visit.
  const deferred = useAppSelector((s) => s.tour.skippedThisSession);
  const active = useAppSelector((s) => s.tour.active);
  const signalOrdersOpen = useAppSelector((s) => s.ui.signalOrdersOpen);
  const strategiesOpen = useAppSelector((s) => s.strategies.viewOpen);

  const ready = status === "succeeded" && profileComplete;

  const seen = (id: TourId) =>
    serverSeen.includes(id) ||
    justSeen.includes(id) ||
    deferred.includes(id);

  // Which tour wants to run right now.
  //
  // The OVERVIEW GOES FIRST, even when a nested view is already open. Ordering
  // "most specific first" meant that anyone who opened Signal or Strategies
  // before the overview had a chance to play lost it to a nested tour — which
  // was exactly the new users it was written for, back when a first visit
  // dropped them straight onto the Signal chart. The nested tours are
  // follow-ups, not replacements.
  let wanted: TourId | null = null;
  if (!seen(TOUR_MAIN)) wanted = TOUR_MAIN;
  else if (strategiesOpen && !seen(TOUR_STRATEGIES)) wanted = TOUR_STRATEGIES;
  else if (signalOrdersOpen && !seen(TOUR_SIGNAL_ORDERS)) wanted = TOUR_SIGNAL_ORDERS;

  const target = wanted;

  // Support hook. A tour that silently decides not to run is impossible to
  // diagnose from a screenshot, so publish the decision and its inputs:
  //   window.__tour            -> why a tour is or is not showing
  //   window.__tour.replay()   -> clear the server's seen list and reload
  useEffect(() => {
    const w = window as unknown as Record<string, unknown>;
    w.__tour = {
      ready,
      meStatus: status,
      profileComplete,
      serverSeen: [...serverSeen],
      justSeen: [...justSeen],
      deferredThisSession: [...deferred],
      active,
      target,
      strategiesViewOpen: strategiesOpen,
      signalOrdersOpen,
      // Which step anchors are on screen RIGHT NOW, per tour.
      anchors: Object.fromEntries(
        ([TOUR_MAIN, TOUR_STRATEGIES, TOUR_SIGNAL_ORDERS] as TourId[]).map((t) => [
          t,
          stepsFor(t).map((st) => `${st.anchor}:${anchorEl(st) ? "found" : "MISSING"}`),
        ]),
      ),
      replay: async () => {
        // Through the api client: app and API are different subdomains, so a
        // relative URL would hit the client server instead.
        await api.resetTours();
        location.reload();
      },
    };
  });

  useEffect(() => {
    if (!ready || active || !target) return;
    let raf = 0;
    const deadline = performance.now() + ANCHOR_WAIT_MS;
    // Wait for at least one anchor. Until then do nothing: starting a tour whose
    // every step is unanchored would immediately close it and mark it seen.
    const look = () => {
      const anchored = stepsFor(target).some((s) => anchorEl(s) !== null);
      if (anchored) {
        dispatch(startTour(target));
        return;
      }
      if (performance.now() < deadline) {
        raf = window.requestAnimationFrame(look);
      }
      // Timed out: leave the tour unseen so it gets another chance next visit.
    };
    raf = window.requestAnimationFrame(look);
    return () => window.cancelAnimationFrame(raf);
  }, [ready, active, target, dispatch]);
}
